import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Phone, EnvelopeSimple, MapPin, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import BlockedDaysCard from "@/components/BlockedDaysCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["new", "confirmed", "done", "cancelled"];
const BADGE = { new: "bg-[#FEF3C7] text-[#92400E]", confirmed: "bg-[#E0EAFF] text-[#15306E]", done: "bg-[#EAF4E3] text-[#2F6B12]", cancelled: "bg-[#F4F4F5] text-[#71717A]" };

const BookingCard = ({ b, onStatus, busy }) => (
  <div data-testid={`admin-booking-${b.id}`} className="border border-[#E4E4E7] rounded-sm p-5 bg-white">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-display font-extrabold text-lg text-[#15306E]">{b.name}</h3>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-sm capitalize ${BADGE[b.status]}`}>{b.status}</span>
        </div>
        <p className="text-sm font-bold text-[#46A01E] mt-1" data-testid={`admin-booking-when-${b.id}`}>{b.pickup_date} · {b.pickup_window}</p>
      </div>
      <div className="text-right">
        <p className="font-display font-black text-2xl text-[#15306E]" data-testid={`admin-booking-total-${b.id}`}>{b.total_estimate != null ? `$${Math.round(b.total_estimate)}` : "—"}</p>
        {b.overnight_surcharge > 0 && <p className="text-xs text-[#B45309]">incl. +{b.overnight_surcharge_pct}% overnight (${Math.round(b.overnight_surcharge)})</p>}
        <p className="text-xs text-[#52525B]">{b.service_type === "curbside" ? "Curbside" : "Full-Service"}</p>
      </div>
    </div>
    <div className="grid sm:grid-cols-3 gap-2 mt-3 text-sm text-[#3F3F46]">
      <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-[#46A01E]"><Phone size={15} weight="fill" className="text-[#46A01E]" /> {b.phone}</a>
      {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#46A01E]"><EnvelopeSimple size={15} weight="fill" className="text-[#46A01E]" /> {b.email}</a>}
      <span className="flex items-center gap-2 sm:col-span-3"><MapPin size={15} weight="fill" className="text-[#46A01E]" /> {b.address}</span>
    </div>
    <p className="mt-3 text-sm text-[#3F3F46]"><strong>Booked:</strong> {b.booking_type === "load" ? b.load_label : b.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
    {b.notes && <p className="mt-1 text-sm text-[#52525B]">{b.notes}</p>}
    {b.status === "new" && (
      <p className="mt-2 text-xs text-[#71717A]" data-testid={`booking-confirm-hint-${b.id}`}>
        {b.email ? `Marking confirmed emails ${b.email} automatically.` : "No email on file — call or text to confirm."}
      </p>
    )}
    {b.photos?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-3" data-testid={`admin-booking-photos-${b.id}`}>
        {b.photos.map((p) => (
          <a key={p.path} href={`${BACKEND}${p.url}`} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-sm overflow-hidden border border-[#E4E4E7]">
            <img src={`${BACKEND}${p.url}?w=320`} loading="lazy" alt={p.filename || "booking photo"} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    )}
    <div className="flex flex-wrap gap-2 mt-4">
      <a href={b.gcal_link} target="_blank" rel="noreferrer" data-testid={`booking-gcal-${b.id}`}
        className="bg-[#15306E] hover:bg-[#0F2350] text-white text-xs font-bold px-3 py-1.5 rounded-sm">Add to Google Calendar</a>
      {STATUSES.filter((s) => s !== b.status).map((s) => (
        <button key={s} data-testid={`booking-status-${s}-${b.id}`} disabled={busy} onClick={() => onStatus(b.id, s)}
          className="border border-[#E4E4E7] hover:border-[#15306E] text-[#15306E] text-xs font-bold px-3 py-1.5 rounded-sm capitalize disabled:opacity-50">Mark {s}</button>
      ))}
    </div>
  </div>
);

export default function BookingsTab() {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/bookings").then(({ data }) => setList(data)).catch(() => setList([]));
  }, []);
  useEffect(load, [load]);

  const onStatus = async (id, status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(data.customer_emailed ? "Booking confirmed — confirmation email sent to the customer." : `Booking marked ${status}.`);
      load();
    }
    catch { toast.error("Update failed."); }
    finally { setBusy(false); }
  };

  if (list === null) return <div className="flex justify-center py-16"><CircleNotch size={32} className="animate-spin text-[#46A01E]" /></div>;
  return (
    <>
      <BlockedDaysCard />
      {list.length === 0 ? (
        <div className="border-2 border-dashed border-[#D4D4D8] rounded-sm py-16 flex flex-col items-center gap-3" data-testid="admin-bookings-empty">
          <CalendarCheck size={40} weight="duotone" className="text-[#46A01E]" />
          <p className="font-display font-bold text-[#15306E]">No pickup bookings yet</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-bookings-list">{list.map((b) => <BookingCard key={b.id} b={b} onStatus={onStatus} busy={busy} />)}</div>
      )}
    </>
  );
}
